import { useEffect, useRef, useState } from 'react';
import { ALL_ORGS, useOrg } from '@/context/OrgContext';
import { ChevronDown, Check as CheckIcon, Filter as FilterIcon } from '@/components/icons';

export default function OrgSwitcher() {
  const { org, orgs, setOrg, label } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', click);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', click);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const options = [ALL_ORGS, ...orgs];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[13px] font-medium text-fg-muted hover:text-fg hover:bg-surface-hover transition-colors max-w-[40vw] sm:max-w-none"
        aria-label="Filter by org"
      >
        <FilterIcon />
        {/* On desktop show "Org | Name ▾"; on mobile just "Name ▾" to save space. */}
        <span className="hidden sm:inline">Org</span>
        <span className="hidden sm:inline text-fg-subtle/60 font-normal">|</span>
        <span className="text-fg truncate">{label(org)}</span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1.5 min-w-[200px] rounded-md bg-bg-elev border border-border/10 shadow-lg p-1 z-50">
          {options.map((o) => {
            const active = o === org;
            return (
              <button
                key={o}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { setOrg(o); setOpen(false); }}
                className={[
                  'w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-[5px] text-[13px] transition-colors',
                  active ? 'text-fg bg-surface-hover' : 'text-fg-muted hover:text-fg hover:bg-surface-hover',
                ].join(' ')}
              >
                <span>{label(o)}</span>
                {active && <CheckIcon />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={10}
      className={`text-fg-subtle transition-transform ${open ? 'rotate-180' : ''}`}
    />
  );
}
